import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';

export type HttpResponseData = {
  statusCode?: number;
  statusText?: string;
  headers?: Record<string, string>;
  body?: string;
};

export function maskSensitiveValue(key: string, value: string): string {
  if (
    key.toLowerCase() === 'authorization' &&
    value.toLowerCase().startsWith('bearer ')
  ) {
    return 'Bearer <token>';
  }

  return value;
}

function formatHeaders(headers?: Record<string, string>): string {
  if (!headers) return '';

  return Object.entries(normalizeHttpHeaders(headers))
    .map(([key, value]) => {
      const headerValue = Array.isArray(value) ? value.join(', ') : value;

      return `${key}: ${maskSensitiveValue(key, headerValue)}`;
    })
    .join('\n');
}

function isCompressedOrBinary(
  body: string,
  headers?: Record<string, string>,
): boolean {
  if (headers) {
    const contentEncoding = Object.entries(headers)
      .find(([key]) => key.toLowerCase() === 'content-encoding')?.[1]
      ?.toLowerCase();

    if (
      contentEncoding &&
      (contentEncoding.includes('gzip') ||
        contentEncoding.includes('deflate') ||
        contentEncoding.includes('br'))
    ) {
      return true;
    }
  }

  if (body && body.length > 0) {
    const nonPrintableCount = [...body].filter((char) => {
      const code = char.charCodeAt(0);

      return code < 32 && code !== 9 && code !== 10 && code !== 13;
    }).length;

    return nonPrintableCount / body.length > 0.1;
  }

  return false;
}

export function formatResponseAsHttp(
  response: HttpResponseData | undefined,
): string {
  if (!response) return '';

  const httpVersion = resolveEffectiveHttpVersion({
    headers: response.headers,
  });

  const statusLine =
    response.statusCode !== undefined
      ? `${httpVersion} ${response.statusCode}${response.statusText ? ` ${response.statusText}` : ''}`
      : httpVersion;

  let content = statusLine;

  if (response.headers) {
    const headerStr = formatHeaders(response.headers);

    if (headerStr) {
      content += `\n${headerStr}`;
    }
  }

  if (response.body) {
    if (isCompressedOrBinary(response.body, response.headers)) {
      content += `\n\n[Response body is compressed/binary and cannot be displayed as text]`;
      content += `\n[Body size: ${response.body.length} bytes]`;
    } else {
      content += `\n\n${response.body}`;
    }
  }

  return `\`\`\`http\n${content}\n\`\`\``;
}
